/**
 * Canonical empty-state primitive.
 *
 * Wraps the legacy `.empty-state .es-emoji/.es-title/.es-msg/.es-cta`
 * markup from crm/index.html. Used for two operational states:
 *
 *   - "no data yet" (onboarding posture)  → role="status" (default)
 *   - "error loading"                     → role="alert"
 *
 * The "no results" filtered-out variant is NOT this primitive — it's
 * an inline table row served by the legacy `.empty-row` class (see
 * ContactsTable / AccountsTable). Different operational meaning.
 *
 * Canon: one primary CTA. An optional `secondaryCta` renders as a quiet
 * `.sec-btn` next to it for the "or do this instead" path (e.g. Add a
 * contact / Import CSV); it never carries equal visual weight.
 */

interface EmptyStateProps {
  title: string
  message: string
  emoji?: string
  cta?: { label: string; onClick: () => void }
  /** Quiet second action; only rendered alongside `cta`. */
  secondaryCta?: { label: string; onClick: () => void }
  /** 'alert' for errors; 'status' (default) for empty/onboarding states. */
  role?: 'alert' | 'status'
}

export function EmptyState({
  title,
  message,
  emoji,
  cta,
  secondaryCta,
  role = 'status',
}: EmptyStateProps) {
  return (
    <div className="empty-state" role={role}>
      {emoji && (
        <div className="es-emoji" aria-hidden="true">
          {emoji}
        </div>
      )}
      <div className="es-title">{title}</div>
      <div className="es-msg">{message}</div>
      {cta && !secondaryCta && (
        <button type="button" className="es-cta" onClick={cta.onClick}>
          {cta.label}
        </button>
      )}
      {cta && secondaryCta && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button type="button" className="es-cta" onClick={cta.onClick}>
            {cta.label}
          </button>
          <button type="button" className="sec-btn" onClick={secondaryCta.onClick}>
            {secondaryCta.label}
          </button>
        </div>
      )}
    </div>
  )
}
